import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import {
  FILE_UPLOAD_LIMITS,
  buildStorageObjectPath,
  fileExtensionOf,
  validateUploadFile,
} from '@dcp/shared'
import type { OrganizationDetails } from '@dcp/shared'

/**
 * Query keys for organization settings
 */
const ORG_SETTINGS_KEYS = {
  details: (orgId: string) => ['organization', 'details', orgId] as const,
}

/**
 * Hook to fetch organization details
 * Used on the organization settings page
 * @param orgId - Organization ID to fetch
 */
export function useOrganizationDetails(orgId: string | undefined) {
  return useQuery({
    queryKey: ORG_SETTINGS_KEYS.details(orgId || ''),
    queryFn: async (): Promise<OrganizationDetails | null> => {
      if (!orgId) return null

      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'get',
            organization_id: orgId,
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch organization details')
      }

      const result = await response.json()
      return result.data as OrganizationDetails
    },
    enabled: !!orgId,
  })
}

/**
 * Hook to update organization settings (name and/or logo_url)
 */
export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      organizationId,
      name,
      logoUrl,
    }: {
      organizationId: string
      name?: string
      logoUrl?: string | null
    }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'update',
            organization_id: organizationId,
            ...(name !== undefined && { name }),
            ...(logoUrl !== undefined && { logo_url: logoUrl }),
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update organization settings')
      }

      return response.json()
    },
    onSuccess: (_, variables) => {
      // Invalidate organization details cache
      queryClient.invalidateQueries({
        queryKey: ORG_SETTINGS_KEYS.details(variables.organizationId),
      })
      // Invalidate accessible organizations cache to update OrganizationSwitcher
      // and all other places that display organization name/logo across the platform
      queryClient.invalidateQueries({
        queryKey: ['accessible-organizations'],
      })
    },
  })
}

/**
 * Logo upload rules, from the shared module rather than a local copy.
 *
 * The copy that used to live here had DRIFTED: it capped uploads at 2 MB while
 * the Organization settings page tells the user "max 5MB" (and the shared
 * constants said 5 MB too). Anyone picking a 3 MB logo was told "File too
 * large. Maximum size is 2MB" — a limit nothing in the UI mentions. One source
 * of truth removes that class of surprise.
 */
const LOGO_RULES = FILE_UPLOAD_LIMITS.ORGANIZATION_LOGO

/**
 * Hook to upload organization logo to Supabase Storage
 * Returns the public URL of the uploaded file
 */
export function useUploadOrganizationLogo() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      file,
    }: {
      organizationId: string
      file: File
    }): Promise<string> => {
      // Validate organization ID format (UUID) to prevent path traversal
      if (
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          organizationId
        )
      ) {
        throw new Error('Invalid organization ID format')
      }

      // Validate the declared MIME type. Kept separate from the extension /
      // size pair below because it inspects `file.type`, which the browser
      // reports and the shared helper does not look at.
      if (!(LOGO_RULES.ALLOWED_TYPES as readonly string[]).includes(file.type)) {
        throw new Error(`Invalid file type. Only ${LOGO_RULES.ALLOWED_TYPES.join(', ')} allowed`)
      }

      // Extension + size, through the same helper the import flow uses.
      const rejection = validateUploadFile(file, LOGO_RULES)
      if (rejection) {
        throw new Error(rejection.message)
      }

      const supabase = getSupabaseClient()

      // Generated path — the user's filename never reaches Storage.
      const fileName = buildStorageObjectPath(
        organizationId,
        fileExtensionOf(file.name)!,
        LOGO_RULES.PATH_PREFIX,
      )

      // Upload to organization-logos bucket
      const { data, error } = await supabase.storage
        .from(LOGO_RULES.BUCKET)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        })

      if (error) {
        throw new Error(`Failed to upload logo: ${error.message}`)
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(LOGO_RULES.BUCKET)
        .getPublicUrl(data.path)

      return urlData.publicUrl
    },
  })
}

/**
 * Hook to delete organization logo from Supabase Storage
 */
export function useDeleteOrganizationLogo() {
  return useMutation({
    mutationFn: async ({
      logoUrl,
    }: {
      logoUrl: string
    }): Promise<void> => {
      const supabase = getSupabaseClient()

      // Extract path from URL
      // URL format: https://xxx.supabase.co/storage/v1/object/public/organization-logos/org-id/logo.png
      // The bucket name comes from the shared rules here too, so a rename can
      // never leave the delete path pointing at the old bucket.
      const urlParts = logoUrl.split(`/${LOGO_RULES.BUCKET}/`)
      if (urlParts.length < 2) {
        throw new Error('Invalid logo URL format')
      }
      const filePath = urlParts[1]

      const { error } = await supabase.storage
        .from(LOGO_RULES.BUCKET)
        .remove([filePath])

      if (error) {
        throw new Error(`Failed to delete logo: ${error.message}`)
      }
    },
  })
}
